import { lazy, ReactNode, Suspense, useEffect, useState } from "react";
import { Redirect, Route, RouteProps, Switch, useLocation } from "react-router";
import { BrowserRouter } from "react-router-dom";
import request, { ResponseError } from "superagent";
import { AdaptiveUi } from "@avinet/adaptive-ui-core";
import { IconList } from "@avinet/adaptive-ui-core/ui/Icon";
import { TranslatorProvider } from "@avinet/adaptive-ui-translate";
import { LoadingMask } from "../components/Loading";
import LoginDialog from "../components/LoginDialog";
import { BACKEND_URL, PUBLIC_URL } from "../config/config";
import { useBrowserLanguage } from "../hooks/useBrowserLanguage";
import useProfile from "../hooks/useProfile";
import useSessionToken from "../hooks/useSessionToken";
import useTranslations from "../hooks/useTranslations";
import EditorLayout from "../layouts/EditorLayout";
import PageLayout from "../layouts/PageLayout";
import { icons } from "../style/icons";
import CollectionView from "../views/CollectionView";
import SurveyCompleteView from "../views/SurveyCompleteView";

const CreateDemoView = lazy(() => import("../views/CreateDemoView"));
const CreateView = lazy(() => import("../views/CreateView"));
const EditView = lazy(() => import("../views/EditView"));
const HomeView = lazy(() => import("../views/HomeView"));
const ResultsView = lazy(() => import("../views/ResultsView"));

const PrivateRoute = (props: RouteProps) => {
  const sessionToken = useSessionToken();
  const [loggedIn, setLoggedIn] = useState(true);
  const [loginKey, setLoginKey] = useState(0);

  useProfile();

  useEffect(() => {
    if (!sessionToken) {
      setLoggedIn(false);
      return;
    }

    const req = request
      .get(BACKEND_URL + "api/info/auth/authenticated")
      .set("Authorization", `Bearer ${sessionToken}`);

    req
      .then(() => setLoggedIn(true))
      .catch((err: ResponseError) => {
        if (err.response?.statusCode === 401) {
          setLoggedIn(false);
        }
      });

    return () => req.abort();
  }, [sessionToken, loginKey]);

  useEffect(() => {
    const tId = setInterval(() => setLoginKey((key) => key + 1), 60000);
    return () => clearInterval(tId);
  }, []);

  const lang = useTranslations(useBrowserLanguage());

  return (
    <>
      {sessionToken && <Route {...props} />}
      {!loggedIn && (
        <TranslatorProvider translations={lang}>
          <LoginDialog />
        </TranslatorProvider>
      )}
    </>
  );
};

const Theme = ({ children }: { children: ReactNode }) => {
  const location = useLocation();

  return (
    <AdaptiveUi
      theme={
        location.pathname.startsWith("/survey/admin") ? "admin" : "adaptive"
      }
      icons={icons}
    >
      {children}
    </AdaptiveUi>
  );
};

const Routes = () => {
  const browserLanguage = useBrowserLanguage();
  const lang = useTranslations(browserLanguage);

  return (
    <BrowserRouter basename={PUBLIC_URL}>
      <Theme>
        <Suspense fallback={<LoadingMask />}>
          <Switch>
            <Route path="/dev/icons">
              <PageLayout>
                <IconList />
              </PageLayout>
            </Route>
            <PrivateRoute path="/survey/admin">
              <TranslatorProvider translations={lang}>
                <Switch>
                  <Route path="/survey/admin/results/:uuid">
                    <EditorLayout>
                      <ResultsView />
                    </EditorLayout>
                  </Route>
                  <Route path="/survey/admin/edit/:uuid">
                    <EditorLayout>
                      <EditView />
                    </EditorLayout>
                  </Route>
                  <Route path="/survey/admin/create/demo">
                    <EditorLayout>
                      <CreateDemoView />
                    </EditorLayout>
                  </Route>
                  <Route path="/survey/admin/create">
                    <EditorLayout>
                      <CreateView />
                    </EditorLayout>
                  </Route>
                  <Route exact path="/survey/admin">
                    <PageLayout>
                      <HomeView />
                    </PageLayout>
                  </Route>
                  <Redirect to="/survey/admin" />
                </Switch>
              </TranslatorProvider>
            </PrivateRoute>
            <Route path="/survey/:uuid/complete">
              <SurveyCompleteView />
            </Route>
            <Route
              exact
              path="/survey/:uuid([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})"
            >
              <CollectionView />
            </Route>
            <Route exact path="/survey/:slug">
              <CollectionView />
            </Route>
            <Redirect to="/survey/admin" />
          </Switch>
        </Suspense>
      </Theme>
    </BrowserRouter>
  );
};

export default Routes;
